<template>
  <div v-if="formTableName" ref="formItemRef" :class="formItemClasses">
    <div :class="ns.e('content')">
      <slot />
      <transition :name="`${ns.namespace.value}-zoom-in-top`">
        <slot v-if="shouldShowError" name="error" :error="validateMessage">
          <div :class="validateClasses">
            {{ validateMessage }}
          </div>
        </slot>
      </transition>
    </div>
  </div>

  <slot v-else />
</template>

<script lang="ts" setup>
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  reactive,
  ref,
  toRefs,
  toRef,
  watch
} from 'vue';
import AsyncValidator from 'async-validator';
import { clone, get, set, castArray as ensureArray } from 'lodash-unified';
import { refDebounced, isFunction } from '@vueuse/core';

import {
  formContextKey,
  formItemContextKey,
  FormItemContext,
  FormItemRule,
  FormValidateFailure,
  FormItemValidateState,
  formItemProps,
  useId,
  useNamespace,
  useSize
} from 'element-plus';

import type { RuleItem } from 'async-validator';

const props = defineProps({
  ...formItemProps,
  fieldValue: {
    required: true,
    validator: () => true
  },

  prop: {
    type: String,
    required: true
  }
});
//const slots = useSlots();

// 得到Form中Table名
const formTableName = inject('formTableName', undefined);

const formContext = inject(formContextKey, undefined);
//const parentFormItemContext = inject(formItemContextKey, undefined);

const _size = useSize(undefined, { formItem: false });
const ns = useNamespace('form-item');

const labelId = useId().value;
const inputIds = ref<string[]>([]);

const validateState = ref<FormItemValidateState>('');
const validateStateDebounced = refDebounced(validateState, 100);
const validateMessage = ref('');
const formItemRef = ref<HTMLDivElement>();
// special inline value.
let initialValue: any = undefined;
let isResettingField = false;

// 属性名，将Form表格名拼上字段名, 比如table名为details, 字段名这num, 拼成details.num
const propName = formTableName ? `${formTableName}.${props.prop}` : '';

const formItemClasses = computed(() => [
  ns.b(),
  ns.m(_size.value),
  ns.is('error', validateState.value === 'error'),
  ns.is('validating', validateState.value === 'validating'),
  ns.is('success', validateState.value === 'success'),
  ns.is('required', isRequired.value || props.required),
  ns.is('no-asterisk', formContext?.hideRequiredAsterisk),
  (formContext as any).requireAsteriskPosition === 'right'
    ? 'asterisk-right'
    : 'asterisk-left',
  { [ns.m('feedback')]: formContext?.statusIcon },
  'easy-form-item'
]);

type Arrayable<T> = T | T[];
function getProp<T = any>(
  obj: Record<string, any>,
  path: Arrayable<string>,
  defaultValue?: any
): { value: T } {
  return {
    get value() {
      return get(obj, path, defaultValue);
    },
    set value(val: any) {
      set(obj, path, val);
    }
  };
}

/*
const _inlineMessage = computed(() =>
  isBoolean(props.inlineMessage)
    ? props.inlineMessage
    : formContext?.inlineMessage || false
);*/

const validateClasses = computed(() => [
  ns.e('error'),
  { [ns.em('error', 'inline')]: formContext?.inlineMessage || false }
]);

/*
const propString = computed(() => {
  if (!props.prop) return '';
  return isString(props.prop) ? props.prop : props.prop.join('.');
});*/

//const isNested = !!parentFormItemContext;

/*const fieldValue = computed(() => {
  const model = formContext?.model;
  if (!model || !props.prop) {
    return;
  }
  return getProp(model, props.prop).value;
});*/

const normalizedRules = computed(() => {
  const { required } = props;

  const rules: FormItemRule[] = [];

  if (props.rules) {
    rules.push(...ensureArray(props.rules));
  }

  const formRules = formContext?.rules;
  if (formRules && propName) {
    const _rules = getProp<Arrayable<FormItemRule> | undefined>(
      formRules,
      propName
    ).value;
    if (_rules) {
      rules.push(...ensureArray(_rules));
    }
  }

  if (required !== undefined) {
    const requiredRules = rules
      .map((rule, i) => [rule, i] as const)
      .filter(([rule]) => Object.keys(rule).includes('required'));

    if (requiredRules.length > 0) {
      for (const [rule, i] of requiredRules) {
        if (rule.required === required) continue;
        rules[i] = { ...rule, required };
      }
    } else {
      rules.push({ required });
    }
  }

  return rules;
});

const validateEnabled = computed(() => normalizedRules.value.length > 0);

const getFilteredRule = (trigger: string) => {
  const rules = normalizedRules.value;
  return (
    rules
      .filter((rule) => {
        if (!rule.trigger || !trigger) return true;
        if (Array.isArray(rule.trigger)) {
          return rule.trigger.includes(trigger);
        } else {
          return rule.trigger === trigger;
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
      .map(({ trigger, ...rule }): RuleItem => rule)
  );
};

const isRequired = computed(() =>
  normalizedRules.value.some((rule) => rule.required)
);

const shouldShowError = computed(
  () =>
    validateStateDebounced.value === 'error' &&
    props.showMessage &&
    (formContext?.showMessage ?? true)
);

const setValidationState = (state: FormItemValidateState) => {
  validateState.value = state;
};

const onValidationFailed = (error: FormValidateFailure) => {
  const { errors, fields } = error;
  if (!errors || !fields) {
    console.error(error);
  }

  setValidationState('error');
  validateMessage.value = errors
    ? errors?.[0]?.message ?? `${propName} is required`
    : '';

  formContext?.emit('validate', propName, false, validateMessage.value);
};

const onValidationSucceeded = () => {
  setValidationState('success');
  formContext?.emit('validate', propName, true, '');
};

const fieldValue = toRef(props, 'fieldValue');
const doValidate = async (rules: RuleItem[]): Promise<true> => {
  //const modelName = props.prop;
  const validator = new AsyncValidator({
    [propName]: rules
  });
  return validator
    .validate({ [propName]: fieldValue.value }, { firstFields: true })
    .then(() => {
      onValidationSucceeded();
      return true as const;
    })
    .catch((err: FormValidateFailure) => {
      onValidationFailed(err as FormValidateFailure);
      return Promise.reject(err);
    });
};

const validate: FormItemContext['validate'] = async (trigger, callback) => {
  // skip validation if its resetting
  if (isResettingField || !propName) {
    return false;
  }

  const hasCallback = isFunction(callback);
  if (!validateEnabled.value) {
    callback?.(false);
    return false;
  }

  const rules = getFilteredRule(trigger);
  if (rules.length === 0) {
    callback?.(true);
    return true;
  }

  setValidationState('validating');

  return doValidate(rules)
    .then(() => {
      callback?.(true);
      return true as const;
    })
    .catch((err: FormValidateFailure) => {
      const { fields } = err;
      callback?.(false, fields);
      return hasCallback ? false : Promise.reject(fields);
    });
};

const clearValidate: FormItemContext['clearValidate'] = () => {
  setValidationState('');
  validateMessage.value = '';
  isResettingField = false;
};

const resetField: FormItemContext['resetField'] = async () => {
  if (!propName) return;

  // prevent validation from being triggered
  isResettingField = true;

  fieldValue.value = clone(initialValue);

  await nextTick();
  clearValidate();

  isResettingField = false;
};

const addInputId: FormItemContext['addInputId'] = (id: string) => {
  if (!inputIds.value.includes(id)) {
    inputIds.value.push(id);
  }
};

const removeInputId: FormItemContext['removeInputId'] = (id: string) => {
  inputIds.value = inputIds.value.filter((listId) => listId !== id);
};

watch(
  () => props.error,
  (val) => {
    validateMessage.value = val || '';
    setValidationState(val ? 'error' : '');
  },
  { immediate: true }
);

watch(
  () => props.validateStatus,
  (val) => setValidationState(val || '')
);

const context: FormItemContext = reactive({
  ...toRefs(props),
  $el: formItemRef,
  size: _size,
  validateState,
  labelId,
  inputIds,
  isGroup: false,
  hasLabel: false,
  addInputId,
  removeInputId,
  resetField,
  clearValidate,
  validate
});

provide(formItemContextKey, context);

onMounted(() => {
  if (propName) {
    formContext?.addField(context);
    initialValue = clone(props.fieldValue);
  }
});

onBeforeUnmount(() => {
  formContext?.removeField(context);
});

defineExpose({
  /** @description form item size */
  size: _size,
  /** @description validation message */
  validateMessage,
  /** @description validation state */
  validateState,
  /** @description validate form item */
  validate,
  /** @description clear validation status */
  clearValidate,
  /** @description reset field value */
  resetField
});
</script>

<style lang="scss" scoped>
.easy-form-item {
  width: 100%;

  &:not(.is-error) {
    margin-bottom: 0px;
  }

  :deep(.el-form-item__error) {
    margin-left: 8px;
  }
}
</style>
